// A library asset's picture on its own: the whole thing fitted into a 9:16 frame, or for avatars cropped to the head.
// Characters have their talking face drawn on, so the same pictures show feelings and lip-sync voice samples.
import { memo } from "react";
import { pictureFit, type View } from "../../engine/camera";
import { pictureFrame } from "../../engine/frame";
import { useLibrary } from "../../library/store";
import type { Emotion, Viseme } from "../../model/face";
import { findAsset, type Asset } from "../../model/library";
import { FrameSvg } from "../../render/FrameSvg";
import { imageSrc } from "../../library/images";
import { useIdleClock } from "./clocks";

// The part of the frame to show: the whole picture with a little room around it, or the head (worked out from the
// face spots the way scripts/body.py does).
function assetView(a: Asset, head: boolean): View {
  const fit = pictureFit(a.picture);
  const f = a.face;
  if (head && f) {
    const drop = f.mouth.y - f.eyes.y;
    const r = Math.max(f.eyes.w * 1.05, drop * 1.6) * fit.s;
    const cx = fit.x + f.eyes.x * fit.s, cy = fit.y + (f.eyes.y + drop * 0.3) * fit.s;
    const w = r * 2.15, h = (w * 16) / 9; // the face fills it: that's how you tell people (and moods) apart
    return { x: cx - w / 2, y: cy - h / 2, w, h };
  }
  const pw = a.picture.w * fit.s, ph = a.picture.h * fit.s;
  const h = Math.max(ph, (pw * 16) / 9) * (a.kind === "background" ? 1 : 1.06), w = (h * 9) / 16;
  return { x: fit.x + pw / 2 - w / 2, y: fit.y + ph / 2 - h / 2, w, h };
}

// A place is a 9:16 reference sheet (older ones are landscape sheets or single views), with no face: shown whole, in the picture's own shape.
export const pictureRatio = (a: Asset | undefined) => (a?.kind === "background" ? a.picture.w / a.picture.h : 9 / 16);
const Place = ({ a }: { a: Asset }) => <img src={imageSrc(a.picture.src)} alt="" draggable={false} style={{ display: "block", width: "100%", height: "100%", objectFit: "contain" }} />;

type Look = { emotion?: Emotion; mouth?: Viseme | null; blink?: boolean };

function draw(a: Asset, head: boolean, clock: number, o: Look) {
  const frame = pictureFrame(a.picture, a.face, { clock, emotion: o.emotion, mouth: o.mouth ?? undefined, blink: o.blink }, assetView(a, head), a.faceStyle);
  return a.kind === "background" ? { ...frame, base: "#2B2E33" } : frame;
}

// A still picture, only redrawn when what it shows changes.
export const Figure = memo(function Figure({ assetId, head = false, ...o }: Look & { assetId: string; head?: boolean }) {
  const a = findAsset(useLibrary(), assetId);
  if (a?.kind === "background") return <Place a={a} />;
  return a ? <FrameSvg id={`fig-${assetId}-${head ? "h" : "f"}`} boil={false} frame={draw(a, head, 0, { blink: false, ...o })} /> : null;
});

// A live picture of an asset that may not be saved yet: blinking, and lip-syncing while a voice sample plays.
export function AssetView({ asset, mouth, id = "asset" }: { asset: Asset; mouth?: Viseme | null; id?: string }) {
  const clock = useIdleClock();
  if (asset.kind === "background") return <Place a={asset} />;
  return <FrameSvg id={id} boil={false} frame={draw(asset, false, clock, { mouth, emotion: mouth ? "happy" : "neutral" })} />;
}

export const Avatar = memo(function Avatar({ assetId, size = 40, ring, emotion = "happy" }: { assetId: string; size?: number; ring?: boolean; emotion?: Emotion }) {
  const a = findAsset(useLibrary(), assetId);
  return (
    <span className={`avatar${ring ? " ring" : ""}`} style={{ width: size, height: size, background: (a?.color ?? "#8C8C99") + "33" }}>
      <Figure assetId={assetId} head emotion={emotion} />
    </span>
  );
});
